import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, Store, ArrowRight, XCircle, Ban, RotateCcw, BadgeCheck, CreditCard, ClipboardList, Link2, Receipt, Briefcase, FileCheck, Languages, Wallet, Calculator, Eye, User, Phone, Mail, Calendar, FileImage, X, Loader2, Bell, Edit3, Filter, Key, Trash2, Image, UserCheck, UserPlus, Shield, Ticket, Copy, Megaphone, TrendingUp, Car, CreditCard as PlateIcon, Home, Settings, LogOut, Plus } from 'lucide-react';
import BreadcrumbNav from '../components/BreadcrumbNav';
import DashboardHeader from '../components/DashboardHeader';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import LanguageToggle from '../components/LanguageToggle';

// Extracted Components
import { UsersSection } from '../components/manage-platform';
import PlatformBreadcrumb, { getParentHub } from './manage-platform/PlatformBreadcrumb';
import useAgentData from './manage-platform/hooks/useAgentData';
import TabRouter from './manage-platform/TabRouter';
import ManagePlatformModals from './manage-platform/ManagePlatformModals';
import useStoreActions from './manage-platform/hooks/useStoreActions';
import useUserActions from './manage-platform/hooks/useUserActions';
import useUserModals from './manage-platform/hooks/useUserModals';
import {
  filterPendingStoresForRequest,
  getStoreRequestCounts as calcStoreRequestCounts,
  getBusinessTypeCounts as calcBusinessTypeCounts,
  filterStoresForManage,
  getStoreManageCounts as calcStoreManageCounts,
} from './manage-platform/utils/storeFilters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Logo
const LOGO_TRIANGLE = '/brand/ast-logo-asb606.png';

// Shared constants + helpers
import {
  openDocument,
  isPdfDocument,
  isImageDocument,
  getStoreTypeName,
  STORE_TYPES,
  BUSINESS_TYPES,
  getStoreStatusLabel,
  STORE_STATUS,
} from '../components/manage-platform/constants';

export default function ManagePlatform() {
  const { platformId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { token, user, logout } = useAuth();
  const { language } = useLanguage();
  const isCloudGM = platformId === 'cloudgm';
  
  // Get initial tab from URL query parameter
  const tabFromUrl = searchParams.get('tab');
  const [activeTabState, setActiveTabState] = useState(tabFromUrl || 'main');
  const activeTab = activeTabState;

  // Sync activeTab when URL search params change (state adjustment during render — React recommended pattern)
  const [prevTabFromUrl, setPrevTabFromUrl] = useState(tabFromUrl);
  if (tabFromUrl !== prevTabFromUrl) {
    setPrevTabFromUrl(tabFromUrl);
    if (tabFromUrl) {
      setActiveTabState(tabFromUrl);
    }
  }

  // Wrapper: updates state + URL search param so refresh / share-link preserve
  // the current tab. Accepts either a value or an updater function (matches the
  // useState setter contract used throughout the codebase).
  const setActiveTab = (next) => {
    const value = typeof next === 'function' ? next(activeTabState) : next;
    setActiveTabState(value);
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      if (value && value !== 'main') {
        params.set('tab', value);
      } else {
        params.delete('tab');
      }
      return params;
    }, { replace: true });
    setPrevTabFromUrl(value && value !== 'main' ? value : null);
  };
  const [users, setUsers] = useState([]);
  const [stores, setStores] = useState([]);
  const [storeRequests, setStoreRequests] = useState([]);
  const [pendingStores, setPendingStores] = useState([]);
  const [serviceRequests, setServiceRequests] = useState([]);
  const [listings, setListings] = useState({ real_estate: [], numbers: [], spare_parts: [], products: [] });
  const [loading, setLoading] = useState(false);
  
  // Notifications
  const [storeNotifications, setStoreNotifications] = useState({ new_pending: 0, total_pending: 0 });
  
  // Store request modals
  const [selectedStore, setSelectedStore] = useState(null);
  const [showStoreDetails, setShowStoreDetails] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showModificationModal, setShowModificationModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [selectedRejectReason, setSelectedRejectReason] = useState('');
  const [modificationMessage, setModificationMessage] = useState('');
  const [selectedModificationReason, setSelectedModificationReason] = useState('');
  const [storeRejectReasons, setStoreRejectReasons] = useState([]);
  const [storeEditReasons, setStoreEditReasons] = useState([]);
  // Store stop/archive flow
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [deactivateReason, setDeactivateReason] = useState('');
  
  // User management modals — all state lives in useUserModals (P3 refactor).
  // `showUserModal` is kept inline because it's only used for the agent-details
  // modal (different domain). The 12 user-modal state slots are flattened off
  // the hook below so existing modal components keep their prop shapes.
  const [showUserModal, setShowUserModal] = useState(false);
  const userModals = useUserModals();
  const {
    selectedUser, setSelectedUser,
    showResetPasswordModal, setShowResetPasswordModal,
    showDeleteUserModal, setShowDeleteUserModal,
    showEditUserModal, setShowEditUserModal,
    showBanUserModal, setShowBanUserModal,
    showResetLinkModal, setShowResetLinkModal,
    showAddUserModal, setShowAddUserModal,
    newPassword, setNewPassword,
    banReason, setBanReason,
    resetLinkData, setResetLinkData,
    editUserData, setEditUserData,
    newUserData, setNewUserData,
    openEditUserModal,
    cancelEdit, cancelResetPassword, cancelDelete,
    cancelBan, cancelAddUser, cancelResetLink,
  } = userModals;
  const [userStatusFilter, setUserStatusFilter] = useState('all'); // all, active, suspended, banned, pending_activation
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('new'); // new, needs_edit, edited
  const [typeFilter, setTypeFilter] = useState('all');
  const [storeRequestDateFrom, setStoreRequestDateFrom] = useState('');
  const [storeRequestDateTo, setStoreRequestDateTo] = useState('');
  const [storeBusinessType, setStoreBusinessType] = useState(null); // null, 'personal' or 'company'
  
  // Store list filter - for stores section (active/rejected)
  const [storeListFilter, setStoreListFilter] = useState('active');
  const [storeManageType, setStoreManageType] = useState(null); // null, 'personal' or 'company' - for manage stores section
  
  // Agent system states
  // Agent lifecycle state + actions — extracted to a custom hook (iter-79).
  // See: pages/manage-platform/hooks/useAgentData.js
  const {
    agentRequests, invitationCodes, approvedAgents,
    processingAgent, agentRequestFilter,
    setAgentRequestFilter,
    fetchAgentData,
    createInvitationCode, deleteInvitationCode, copyCode,
    approveAgentRequest, rejectAgentRequest, revokeAgent,
    blockAgent, unblockAgent, archiveAgent,
    getFilteredAgentRequests,
  } = useAgentData(token);

  // Agent modals — UI state stays at the page level (not in the hook)
  const [showInviteCodeModal, setShowInviteCodeModal] = useState(false);
  const [showCreateCodeModal, setShowCreateCodeModal] = useState(false);
  const [showRejectAgentModal, setShowRejectAgentModal] = useState(false);
  const [selectedAgentRequest, setSelectedAgentRequest] = useState(null);
  const [agentRejectReason, setAgentRejectReason] = useState('');
  
  // New code form
  const [newCodeMaxUses, setNewCodeMaxUses] = useState(1);
  const [newCodeExpiresDays, setNewCodeExpiresDays] = useState(30);
  const [creatingCode, setCreatingCode] = useState(false);

  // Phone Numbers Market States
  const [phoneNumbersTab, setPhoneNumbersTab] = useState('displayed'); // 'displayed' or 'activity'
  const [displayedNumbers, setDisplayedNumbers] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loadingNumbers, setLoadingNumbers] = useState(false);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [selectedNumber, setSelectedNumber] = useState(null);
  const [showEditNumberModal, setShowEditNumberModal] = useState(false);
  const [showDeleteNumberModal, setShowDeleteNumberModal] = useState(false);
  const [showBlockNumberModal, setShowBlockNumberModal] = useState(false);
  const [processingNumber, setProcessingNumber] = useState(null);

  // Get headers with current token
  const getHeaders = () => {
    if (!token) return {};
    return { Authorization: `Bearer ${token}` };
  };

  // ===================== Phone Numbers Market Functions =====================
  
  // جلب الأرقام المعروضة
  const fetchDisplayedNumbers = async () => {
    const headers = getHeaders();
    setLoadingNumbers(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/market/phone-numbers`, { headers });
      setDisplayedNumbers(res.data.data || []);
    } catch (err) {
      console.log('Error fetching displayed numbers:', err.message);
      setDisplayedNumbers([]);
    } finally {
      setLoadingNumbers(false);
    }
  };

  // جلب حركة الأرقام
  const fetchActivityLogs = async () => {
    const headers = getHeaders();
    setLoadingActivity(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/market/activity-logs`, { headers });
      setActivityLogs(res.data.data || []);
    } catch (err) {
      console.log('Error fetching activity logs:', err.message);
      setActivityLogs([]);
    } finally {
      setLoadingActivity(false);
    }
  };

  // حذف رقم
  const deleteNumber = async (numberId) => {
    const headers = getHeaders();
    setProcessingNumber(numberId);
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/market/phone-numbers/${numberId}`, { headers });
      toast.success('تم حذف الرقم بنجاح');
      fetchDisplayedNumbers();
      setShowDeleteNumberModal(false);
      setSelectedNumber(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل في حذف الرقم');
    } finally {
      setProcessingNumber(null);
    }
  };

  // بلوك رقم
  const blockNumber = async (numberId) => {
    const headers = getHeaders();
    setProcessingNumber(numberId);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/market/phone-numbers/${numberId}/block`, {}, { headers });
      toast.success('تم حظر الرقم بنجاح');
      fetchDisplayedNumbers();
      setShowBlockNumberModal(false);
      setSelectedNumber(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل في حظر الرقم');
    } finally {
      setProcessingNumber(null);
    }
  };

  // ===================== Store Dropdown Reasons (for StoreRejectModal / ModificationModal) =====================
  // Admin-side CRUD for ALL dropdown reasons + Store Types + Categories + Subcategories was
  // moved to dedicated pages (ControlCloudGMBusiness.js / StoreTypesManagement.js / CategoriesManagement.js)
  // in earlier iterations. The ~400 lines of inline CRUD were dead code and removed in iter-82.
  // The only piece kept here is `fetchStoreDropdownReasons` — the loadData useEffect calls it to
  // populate the rejection/edit reason dropdowns inside StoreRejectModal and ModificationModal.
  // جلب أسباب القوائم المنسدلة للمتاجر
  const fetchStoreDropdownReasons = async () => {
    try {
      const [editRes, rejectRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/dropdown-reasons/edit_request`),
        axios.get(`${BACKEND_URL}/api/dropdown-reasons/rejection`),
      ]);
      setStoreEditReasons(editRes.data.data || []);
      setStoreRejectReasons(rejectRes.data.data || []);
    } catch (err) {
      console.log('Error fetching store dropdown reasons:', err.message);
    }
  };
  // ===================== Agent System — modal callbacks =====================
  // The actual data + actions live in useAgentData() (top of file). These two
  // thin wrappers translate modal UI state into the hook's typed signatures.

  const handleCreateInvitationCode = async () => {
    setCreatingCode(true);
    const ok = await createInvitationCode({ maxUses: newCodeMaxUses, expiresInDays: newCodeExpiresDays });
    setCreatingCode(false);
    if (ok) {
      setShowCreateCodeModal(false);
      setNewCodeMaxUses(1);
      setNewCodeExpiresDays(30);
    }
  };

  const handleConfirmRejectAgent = async () => {
    const ok = await rejectAgentRequest(selectedAgentRequest, agentRejectReason);
    if (ok) {
      setShowRejectAgentModal(false);
      setSelectedAgentRequest(null);
      setAgentRejectReason('');
    }
  };

  // تحديد معلومات المنصة
  const getPlatformInfo = () => {
    if (platformId === 'cloudgm') {
      return {
        name: 'CloudGM',
        nameAr: 'منصة العملاء',
        nameEn: 'Customer Platform',
        filterPlatform: 'cloudgm',
        filterRole: 'customer',
        userLabel: 'العملاء',
        userLabelEn: 'Customers',
        logo: '/brand/ast-logo-cloudgm.png'
      };
    } else if (platformId === 'cloudgm-business') {
      return {
        name: 'CloudGM Business',
        nameAr: 'منصة التجار',
        nameEn: 'Merchant Platform',
        filterPlatform: 'cloudgm_business',
        filterRole: 'business',
        userLabel: 'الحسابات',
        userLabelEn: 'Accounts',
        logo: '/brand/ast-logo-cloudgm-business.png'
      };
    } else {
      return {
        name: 'ASB606',
        nameAr: 'إدارة الشركة',
        nameEn: 'Company Management',
        filterPlatform: 'all',
        filterRole: 'all',
        userLabel: 'المستخدمين',
        userLabelEn: 'Users',
        logo: null
      };
    }
  };
  
  const platformInfo = getPlatformInfo();

  // Scroll to top on tab change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeTab]);

  // Fetch data
  useEffect(() => {
    const loadData = async () => {
      if (!token) {
        console.log('No token, waiting...');
        return;
      }
      
      // Get platform info inside useEffect to ensure fresh values
      let filterPlatform = 'all';
      let filterRole = 'all';
      
      if (platformId === 'cloudgm') {
        filterPlatform = 'cloudgm';
        filterRole = 'customer';
      } else if (platformId === 'cloudgm-business') {
        filterPlatform = 'cloudgm_business';
        filterRole = 'business';
      }
      
      const headers = { Authorization: `Bearer ${token}` };
      setLoading(true);
      
      try {
        let usersUrl = `${BACKEND_URL}/api/admin/users`;
        if (filterPlatform !== 'all') {
          usersUrl += `?platform=${filterPlatform}&role=${filterRole}`;
        }
        
        console.log('Fetching users from:', usersUrl);
        
        const [usersRes, storesRes, pendingStoresRes, requestsRes, reRes, numRes, spRes, prodRes, notifRes] = await Promise.all([
          axios.get(usersUrl, { headers }),
          axios.get(`${BACKEND_URL}/api/admin/stores`, { headers }),
          axios.get(`${BACKEND_URL}/api/admin/stores?status=pending_review`, { headers }),
          axios.get(`${BACKEND_URL}/api/admin/service-requests`, { headers }),
          axios.get(`${BACKEND_URL}/api/admin/real-estate`, { headers }),
          axios.get(`${BACKEND_URL}/api/admin/numbers`, { headers }),
          axios.get(`${BACKEND_URL}/api/admin/spare-parts`, { headers }),
          axios.get(`${BACKEND_URL}/api/admin/products`, { headers }),
          axios.get(`${BACKEND_URL}/api/admin/store-notifications`, { headers }),
        ]);
        
        console.log('Users response:', usersRes.data);
        console.log('Stores response:', storesRes.data);
        console.log('Pending stores response:', pendingStoresRes.data);
        
        setUsers(usersRes.data.data || []);
        setStores(storesRes.data.data || []);
        setStoreRequests([]); // لم نعد نستخدم store_requests منفصلة
        setPendingStores(pendingStoresRes.data.data || []);
        setServiceRequests(requestsRes.data.data || []);
        setListings({
          real_estate: reRes.data.data || [],
          numbers: numRes.data.data || [],
          spare_parts: spRes.data.data || [],
          products: prodRes.data.data || [],
        });
        setStoreNotifications(notifRes.data || { new_pending: 0, total_pending: 0 });
        
        // جلب أسباب القوائم المنسدلة للمتاجر
        fetchStoreDropdownReasons();
        
        if (notifRes.data?.has_new && platformId === 'cloudgm-business') {
          toast.info(`يوجد ${notifRes.data.new_pending} طلب متجر جديد`, { duration: 5000, icon: '🔔' });
        }
        
        // جلب بيانات نظام الوكلاء لـ CloudGM Business — مرجع لـ hook الوحيد (iter-79)
        if (platformId === 'cloudgm-business') {
          fetchAgentData();
        }
        
        // جلب بيانات أرقام الهواتف والحركات لـ CloudGM (منصة العملاء)
        if (platformId === 'cloudgm') {
          try {
            const [numbersRes, logsRes] = await Promise.all([
              axios.get(`${BACKEND_URL}/api/admin/market/phone-numbers`, { headers }),
              axios.get(`${BACKEND_URL}/api/admin/market/activity-logs`, { headers }),
            ]);
            setDisplayedNumbers(numbersRes.data.data || []);
            setActivityLogs(logsRes.data.data || []);
          } catch (err) {
            console.log('Error loading market data:', err.message);
          }
        }
      } catch (err) {
        console.error('Data loading error:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [token, platformId]);

  // Refresh data function
  const refreshData = async () => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      let usersUrl = `${BACKEND_URL}/api/admin/users`;
      if (platformInfo.filterPlatform !== 'all') {
        usersUrl += `?platform=${platformInfo.filterPlatform}&role=${platformInfo.filterRole}`;
      }
      
      // تحديث المستخدمين والمتاجر معاً
      const [usersRes, storesRes, pendingStoresRes] = await Promise.all([
        axios.get(usersUrl, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/stores`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/stores?status=pending_review`, { headers }),
      ]);
      
      setUsers(usersRes.data.data || []);
      setStores(storesRes.data.data || []);
      setPendingStores(pendingStoresRes.data.data || []);
    } catch (err) {
      console.log('Refresh error:', err.message);
    }
  };

  // Approve store
  // ===================== Store Actions (lifecycle) =====================
  // All store mutations (approve / reject / request-edit / deactivate / archive)
  // live in useStoreActions. The hook also owns the `processingStore` busy state
  // and handles modal-close + toast + refresh in the right order to avoid the
  // black-screen race we hit pre Round 36.
  const {
    processingStore,
    approveStore,
    rejectStore: doRejectStore,
    requestModification: doRequestModification,
    deactivateStore,
    archiveStore,
    cascadeDeleteStore,
  } = useStoreActions({
    token,
    language,
    refreshData,
    setShowStoreDetails,
    setSelectedStore,
    setShowRejectModal,
    setShowModificationModal,
    setShowDeactivateModal,
    setRejectReason,
    setSelectedRejectReason,
    setModificationMessage,
    setSelectedModificationReason,
    setDeactivateReason,
  });

  // Adapter: existing modals call onConfirm() with no args; bridge to hook signature.
  const rejectStore = () => doRejectStore({
    store: selectedStore,
    reasonCategory: selectedRejectReason,
    reasonDetail: rejectReason,
  });
  const requestModification = () => doRequestModification({
    store: selectedStore,
    reasonCategory: selectedModificationReason,
    reasonMessage: modificationMessage,
  });

  // Filter stores based on status and type - for store requests (pending/needs_edit/resubmitted ONLY)
  const getFilteredStores = () => filterPendingStoresForRequest({
    pendingStores,
    storeBusinessType,
    statusFilter,
    typeFilter,
    dateFrom: storeRequestDateFrom,
    dateTo: storeRequestDateTo,
  });

  const getStoreRequestCounts = () => calcStoreRequestCounts({ pendingStores, storeBusinessType });
  const getBusinessTypeCounts = () => calcBusinessTypeCounts({ pendingStores });
  const getStoresListFiltered = () => filterStoresForManage({ stores, storeManageType, storeListFilter });
  const getStoreManageCounts = () => calcStoreManageCounts({ stores });

  // ===================== User Management Functions =====================
  
  // إيقاف/تفعيل حساب المستخدم
  // ===================== User Actions =====================
  // All user mutations live in useUserActions. The hook owns `processingUser`
  // and follows the same close-modal-then-await-refresh pattern as
  // useStoreActions to keep the race-free invariant uniform across the page.
  const {
    processingUser,
    toggleUserStatus,
    resetUserPassword: doResetUserPassword,
    updateUserData: doUpdateUserData,
    toggleBanUser: doToggleBanUser,
    sendResetLink: doSendResetLink,
    createNewUser: doCreateNewUser,
    deleteUser: doDeleteUser,
  } = useUserActions({
    token,
    language,
    refreshData,
    isCloudGM,
    setSelectedUser,
    setShowResetPasswordModal,
    setNewPassword,
    setShowEditUserModal,
    setShowBanUserModal,
    setBanReason,
    setResetLinkData,
    setShowResetLinkModal,
    setShowAddUserModal,
    setNewUserData,
    setShowDeleteUserModal,
  });

  // Adapters: existing modals call these with no args; bridge to hook signatures.
  // The `*ForUser(user)` variants accept an explicit user to avoid stale-closure
  // bugs on row buttons that previously did `setSelectedUser(user); action();`.
  const resetUserPassword = () => doResetUserPassword({ user: selectedUser, newPassword });
  const updateUserData = () => doUpdateUserData({ user: selectedUser, data: editUserData });
  const toggleBanUser = (shouldBan = true) => doToggleBanUser({ user: selectedUser, shouldBan, reason: banReason });
  const sendResetLink = (user) => doSendResetLink({ user: user || selectedUser });
  const unbanUserDirect = (user) => doToggleBanUser({ user, shouldBan: false, reason: '' });
  const createNewUser = () => doCreateNewUser({ newUserData });
  const deleteUser = () => doDeleteUser({ user: selectedUser });

  const formatDate = (dateStr) => {
    if (!dateStr) return language === 'ar' ? 'غير محدد' : 'Not specified';
    try {
      const d = new Date(dateStr);
      if (Number.isNaN(d.getTime())) return language === 'ar' ? 'غير محدد' : 'Not specified';
      // Always use English digits (en-GB) regardless of UI language
      return d.toLocaleString('en-GB', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return language === 'ar' ? 'غير محدد' : 'Not specified';
    }
  };

  const verifyStore = async (storeId, verified) => {
    const headers = getHeaders();
    try {
      await axios.put(`${BACKEND_URL}/api/admin/stores/${storeId}/verify`, { verified }, { headers });
      toast.success(verified ? 'تم توثيق المتجر' : 'تم رفض المتجر');
      refreshData();
    } catch {
      toast.error('حدث خطأ');
    }
  };

  const updateRequestStatus = async (requestId, status) => {
    const headers = getHeaders();
    try {
      await axios.put(`${BACKEND_URL}/api/admin/service-requests/${requestId}/status`, { status }, { headers });
      toast.success('تم تحديث حالة الطلب');
      refreshData();
    } catch {
      toast.error('حدث خطأ');
    }
  };

  const updateStoreRequestStatus = async (requestId, status) => {
    const headers = getHeaders();
    try {
      await axios.put(`${BACKEND_URL}/api/admin/store-requests/${requestId}/status`, { status }, { headers });
      toast.success(status === 'approved' ? 'تم قبول الطلب' : 'تم رفض الطلب');
      refreshData();
    } catch {
      toast.error('حدث خطأ');
    }
  };

  const statusBadge = (status) => {
    const map = {
      'active': { label: 'نشط', cls: 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' },
      'pending_activation': { label: 'بانتظار التفعيل', cls: 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30' },
      'suspended': { label: 'موقوف', cls: 'bg-orange-500/20 text-orange-400 border border-orange-500/30' },
      'banned': { label: 'محظور', cls: 'bg-red-500/20 text-red-400 border border-red-500/30' },
      'pending': { label: 'قيد المراجعة', cls: 'bg-amber-500/20 text-amber-400 border border-amber-500/30' },
      'approved': { label: 'مقبول', cls: 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' },
      'rejected': { label: 'مرفوض', cls: 'bg-red-500/20 text-red-400 border border-red-500/30' },
      'in_progress': { label: 'قيد التنفيذ', cls: 'bg-sky-500/20 text-sky-400 border border-sky-500/30' },
      'completed': { label: 'مكتمل', cls: 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' },
    };
    const s = map[status] || { label: status, cls: 'bg-gray-500/20 text-gray-400 border border-gray-500/30' };
    return <span className={`px-3 py-1 rounded-full text-xs font-semibold ${s.cls}`}>{s.label}</span>;
  };

  // Sub-page back navigation: clear inner state before leaving the tab.
  const handleBack = () => {
    if (activeTab === 'store-requests' && storeBusinessType !== null) {
      setStoreBusinessType(null);
      return;
    }
    if (activeTab === 'stores' && storeManageType !== null) {
      setStoreManageType(null);
      return;
    }
    if (activeTab === 'main') {
      // Return to FZLC platform selection (customers/merchants buttons)
      navigate('/dashboard?platform=fzlc');
    } else {
      const parent = getParentHub(activeTab);
      setActiveTab(parent || 'main');
    }
  };

  return (
    <div className="min-h-screen bg-[#030303]">
      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-[#d4af37]/5 rounded-full blur-[150px]" />
      </div>

      {/* Unified Header */}
      <DashboardHeader
        title={platformInfo.name}
        subtitle={language === 'ar' ? platformInfo.nameAr : platformInfo.nameEn}
        icon={isCloudGM ? Users : Store}
        iconColor={isCloudGM ? '#d4af37' : '#8B5CF6'}
        onBack={handleBack}
      />

      {/* Breadcrumb (extracted to <PlatformBreadcrumb />) */}
      <PlatformBreadcrumb
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        isCloudGM={isCloudGM}
        language={language}
        storeBusinessType={storeBusinessType}
        setStoreBusinessType={setStoreBusinessType}
        storeManageType={storeManageType}
        setStoreManageType={setStoreManageType}
      />

      {/* Main Content */}
      <main className="relative z-10 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Tab routing — extracted to <TabRouter /> in iter-80 */}
        <TabRouter ctx={{
          // Routing
          activeTab, setActiveTab,
          language, isCloudGM, navigate, getHeaders,
          // Users / accounts
          users, loading, userStatusFilter, setUserStatusFilter, setShowAddUserModal,
          platformInfo, statusBadge, openEditUserModal, setSelectedUser, setShowResetPasswordModal,
          sendResetLink, unbanUserDirect, processingUser, toggleUserStatus, toggleBanUser,
          setShowBanUserModal, setShowDeleteUserModal,
          onRefreshUsers: refreshData,   // iter-269 — soft refresh (was window.location.reload)
          // Stores
          pendingStores, stores, storeBusinessType, setStoreBusinessType, storeNotifications,
          statusFilter, setStatusFilter, typeFilter, setTypeFilter,
          storeRequestDateFrom, setStoreRequestDateFrom, storeRequestDateTo, setStoreRequestDateTo,
          formatDate, processingStore, approveStore,
          setSelectedStore, setShowStoreDetails, setShowModificationModal, setShowRejectModal,
          storeManageType, setStoreManageType, storeListFilter, setStoreListFilter,
          getBusinessTypeCounts, getFilteredStores, getStoreRequestCounts,
          getStoreManageCounts, getStoresListFiltered,
          // Agents
          agentRequests, invitationCodes, approvedAgents,
          agentRequestFilter, setAgentRequestFilter, getFilteredAgentRequests,
          approveAgentRequest, revokeAgent, blockAgent, unblockAgent, archiveAgent,
          copyCode, deleteInvitationCode,
          processingAgent, setSelectedAgentRequest, setShowRejectAgentModal, setShowCreateCodeModal,
          token,  // ← passed for AgentAnalyticsBanner
          // Market numbers
          phoneNumbersTab, setPhoneNumbersTab, displayedNumbers, loadingNumbers,
          activityLogs, loadingActivity, selectedNumber, setSelectedNumber, processingNumber,
          setShowEditNumberModal, setShowDeleteNumberModal, setShowBlockNumberModal,
          fetchDisplayedNumbers, fetchActivityLogs,
          // Services & legacy listings
          serviceRequests, updateRequestStatus, listings,
        }} />

        </div>
      </main>

      {/* All modals — extracted to <ManagePlatformModals /> in iter-81 (dead StoreType/Category renders pruned in iter-82) */}
      <ManagePlatformModals ctx={{
        // Store modals
        showStoreDetails, setShowStoreDetails, selectedStore,
        STORE_TYPES, BUSINESS_TYPES, isPdfDocument, isImageDocument, openDocument, formatDate, language,
        setShowModificationModal, setShowRejectModal, approveStore, processingStore,
        setShowDeactivateModal, archiveStore, cascadeDeleteStore, showDeactivateModal, deactivateReason, setDeactivateReason,
        deactivateStore, showRejectModal, storeRejectReasons, selectedRejectReason, setSelectedRejectReason,
        rejectReason, setRejectReason, rejectStore,
        showModificationModal, storeEditReasons, selectedModificationReason, setSelectedModificationReason,
        modificationMessage, setModificationMessage, requestModification,
        // User modals
        showEditUserModal, setShowEditUserModal, selectedUser, editUserData, setEditUserData,
        cancelEdit, updateUserData, processingUser,
        showResetPasswordModal, setShowResetPasswordModal, newPassword, setNewPassword,
        cancelResetPassword, resetUserPassword,
        showDeleteUserModal, setShowDeleteUserModal, cancelDelete, deleteUser,
        showBanUserModal, setShowBanUserModal, banReason, setBanReason, cancelBan, toggleBanUser,
        showAddUserModal, setShowAddUserModal, newUserData, setNewUserData, cancelAddUser, createNewUser,
        showResetLinkModal, setShowResetLinkModal, resetLinkData, cancelResetLink,
        // Agent modals
        showCreateCodeModal, setShowCreateCodeModal, newCodeMaxUses, setNewCodeMaxUses,
        newCodeExpiresDays, setNewCodeExpiresDays, handleCreateInvitationCode, creatingCode,
        showRejectAgentModal, setShowRejectAgentModal, selectedAgentRequest, setSelectedAgentRequest,
        agentRejectReason, setAgentRejectReason, handleConfirmRejectAgent, processingAgent,
        // Number modals
        showDeleteNumberModal, setShowDeleteNumberModal, selectedNumber, setSelectedNumber,
        deleteNumber, processingNumber,
        showBlockNumberModal, setShowBlockNumberModal, blockNumber,
        showEditNumberModal, setShowEditNumberModal,
      }} />
    </div>
  );
}
